import { c as _c } from "react/compiler-runtime";
import { trackEvent } from '@svs/nova-core/nextjs/analytics';
import { type FormAction, useForm, type UseMutationResult } from '@svs/nova-poolbetting/core/tanstack';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { CouponSettings, SettingsKeys } from '@svs/nova-poolbetting/schema/sessionstore';
import type { Tipsinfo } from '@svs/nova-poolbetting/schema/tipsinfo';
import { CheckboxGroup, CheckboxGroupItem } from '@svs/nova-poolbetting/ui/FormComponents';
import * as TipsenSheet from '@svs/nova-poolbetting/ui/TipsenSheet';
import { Fragment, type PropsWithChildren, type ReactNode } from 'react';
import FormSubmitButton from './FornSubmitButton';
type CouponSettingsFormProps<TData> = PropsWithChildren<{
  action: FormAction<TData>;
  userSettings: CouponSettings;
  tipsinfo: Definition['tipsinfoOptions'];
  mutationApi: UseMutationResult<TData, Error, FormData>;
  settingsKey: SettingsKeys;
}>;
const FORM_ID = 'couponSettings';
const couponSettingsTitleMap: Record<Exclude<Tipsinfo, 'Analysis'>, string> = {
  Odds: 'Odds',
  SvenskaFolket: 'Svenska folket',
  StartOdds: 'Startodds',
  Matchstart: 'Matchstart',
  FavoriteOdds: 'Favoritskap utifrån odds',
  Mutuals: 'Inbördes möten'
};

/**
 * Coupon settings form items.
 *
 * @param root0 component props
 * @param root0.action Form action to run upon submit
 * @param root0.userSettings Current user settings
 * @param root0.tipsinfo Tipsinfo values to render
 * @param root0.mutationApi Mutation API instance
 * @param root0.settingsKey Settings key, to get settings for either play or my bets/results
 * @returns React component for coupon setting form items
 */
export default function CouponSettingsForm(t0) {
  const $ = _c(29);
  const {
    action,
    userSettings,
    tipsinfo,
    mutationApi,
    settingsKey
  } = t0;
  let t1;
  if ($[0] !== tipsinfo) {
    t1 = tipsinfo.filter(_temp);
    $[0] = tipsinfo;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tipsinfoOptions = t1;
  const hasAnalysis = tipsinfo.length !== tipsinfoOptions.length;
  let t2;
  if ($[2] !== mutationApi || $[3] !== userSettings) {
    t2 = {
      mutationApi,
      defaultValues: userSettings
    };
    $[2] = mutationApi;
    $[3] = userSettings;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const form = useForm(t2);
  let t3;
  if ($[5] !== form.Field || $[6] !== settingsKey) {
    t3 = settingsKey === "MY_BETS" && <form.Field name="correction">{_temp4}</form.Field>;
    $[5] = form.Field;
    $[6] = settingsKey;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== form.Field || $[9] !== hasAnalysis) {
    t4 = hasAnalysis && <form.Field name="gameAnalysis">{_temp6}</form.Field>;
    $[8] = form.Field;
    $[9] = hasAnalysis;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== tipsinfoOptions || $[12] !== userSettings) {
    t5 = t6 => {
      const {
        state: state_1,
        handleChange: handleChange_1
      } = t6;
      return <CheckboxGroup title="Information i kupongen"><CheckboxGroupItem name="tipsinfoFull" id="tipsinfoFull" value="on" checked={tipsinfoOptions.every(tipsinfoItem => state_1.includes(tipsinfoItem))} onCheckedChange={isChecked_1 => {
          handleChange_1(isChecked_1 ? tipsinfoOptions : []);
        }}>All info</CheckboxGroupItem>{tipsinfoOptions.map(tipsinfoValue => {
          const isDisabled = tipsinfoValue === "SvenskaFolket" && userSettings.outcomeLabel === "LocalDistribution";
          return <Fragment key={tipsinfoValue}><CheckboxGroupItem name="tipsinfo" id={`tipsinfo${tipsinfoValue}`} disabled={isDisabled} value={tipsinfoValue} checked={state_1.includes(tipsinfoValue)} onCheckedChange={isChecked_2 => {
              if (!isChecked_2) {
                handleChange_1(state_1.filter(value_2 => value_2 !== tipsinfoValue));
                return;
              }
              handleChange_1(state_1.concat(tipsinfoValue));
            }}>{couponSettingsTitleMap[tipsinfoValue]}</CheckboxGroupItem>{isDisabled && state_1.includes("SvenskaFolket") && <input type="hidden" name="tipsinfo" value="SvenskaFolket" />}</Fragment>;
        })}</CheckboxGroup>;
    };
    $[11] = tipsinfoOptions;
    $[12] = userSettings;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== form.Field || $[15] !== t5) {
    t6 = <form.Field name="tipsinfo">{t5}</form.Field>;
    $[14] = form.Field;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== form.Field) {
    t7 = <form.Field name="outcomeLabel">{_temp7}</form.Field>;
    $[17] = form.Field;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t6 || $[22] !== t7) {
    t8 = <TipsenSheet.Content>{t3}{t4}{t6}{t7}</TipsenSheet.Content>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <TipsenSheet.Footer><FormSubmitButton form={FORM_ID}>Spara</FormSubmitButton></TipsenSheet.Footer>;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== action || $[26] !== form.Form || $[27] !== t8) {
    t10 = <form.Form className="grid h-full grid-rows-[auto_1fr_auto]" action={action} id={FORM_ID}>{t8}{t9}</form.Form>;
    $[25] = action;
    $[26] = form.Form;
    $[27] = t8;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
function _temp7(t0) {
  const {
    state: state_2,
    handleChange: handleChange_2
  } = t0;
  return <CheckboxGroup title={"Kuponginst\xE4llningar"}><CheckboxGroupItem id="outcomeLabel" name="outcomeLabel" value="LocalDistribution" checked={state_2 === "LocalDistribution"} onCheckedChange={() => {
      trackEvent({
        category: "Nya Tipsen - Inst\xE4llningar",
        action: "Inneh\xE5ll i knappar",
        optValue: state_2 === "LocalDistribution" ? "1X2" : "Svenska folket (%)"
      });
      handleChange_2(state_2 === "LocalDistribution" ? "Outcome" : "LocalDistribution");
    }}>Svenska folket (%) i utfall</CheckboxGroupItem></CheckboxGroup>;
}
function _temp6(t0) {
  const {
    state: state_0,
    handleChange: handleChange_0
  } = t0;
  return <CheckboxGroup title="Spelanalyser"><CheckboxGroupItem name="gameAnalysis" value="Tip" checked={state_0.includes("Tip")} onCheckedChange={() => {
      handleChange_0(state_0.includes("Tip") ? state_0.filter(_temp5) : [...state_0, "Tip"]);
    }}>Speltips</CheckboxGroupItem></CheckboxGroup>;
}
function _temp5(value_1) {
  return value_1 !== "Tip";
}
function _temp4(t0) {
  const {
    state,
    handleChange
  } = t0;
  return <CheckboxGroup title={"R\xE4ttningsinst\xE4llningar"}><CheckboxGroupItem id="correctionOngoing" name="correction" value="Live" checked={state.includes("Live")} onCheckedChange={isChecked => {
      if (!isChecked) {
        handleChange(state.filter(_temp2));
        return;
      }
      handleChange(state.concat("Live"));
    }}>Pågående spel - Automatisk rättning</CheckboxGroupItem><CheckboxGroupItem id="correctionFinished" value="Finished" name="correction" checked={state.includes("Finished")} onCheckedChange={isChecked_0 => {
      if (!isChecked_0) {
        handleChange(state.filter(_temp3));
        return;
      }
      handleChange(state.concat("Finished"));
    }}>Avgjorda spel - Automatisk rättning</CheckboxGroupItem></CheckboxGroup>;
}
function _temp3(value_0) {
  return value_0 !== "Finished";
}
function _temp2(value) {
  return value !== "Live";
}
function _temp(option) {
  return option !== "Analysis";
}